import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent, SyntheticEvent } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { s3Asset } from '../../utils/s3Assets'
import './AnnouncementBanner.css'

// Max card tilt in degrees.
const MAX_TILT = 10

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Optimised WebP copies of the banner (145 KB / 54 KB), served from /public so
// they ship with the site and load fast. The full-size S3 original is kept only
// as a fallback if a WebP ever fails to load.
const BANNER_SRC = '/assets/announcement/aglf-banner-1760.webp'
const BANNER_SRCSET = '/assets/announcement/aglf-banner-960.webp 960w, /assets/announcement/aglf-banner-1760.webp 1760w'
const BANNER_SIZES = '(max-width: 920px) 100vw, 880px'
const BANNER_THUMB = '/assets/announcement/aglf-banner-thumb.webp'
const BANNER_FALLBACK = s3Asset('AGLF_Original_Banner.jpg.jpeg')
// Intrinsic aspect ratio so the card reserves its height before the image paints.
const BANNER_WIDTH = 1760
const BANNER_HEIGHT = 620
// Where "Read More" takes the visitor. Internal routes use <Link>, full URLs
// open in a new tab.
const READ_MORE_HREF = 'https://aglf.bigdataghana.com/'
// Tiny pause after the intro loader so the modal doesn't land mid-transition.
// Minimum pause after the intro loader before the modal can open, so the
// homepage gets a chance to mount + paint first and the banner never lands on
// a blank page.
const OPEN_DELAY_MS = 450
// Hard cap: open no later than this after mount even if the page is still busy
// or the image hasn't finished (it's preloaded in index.html, so this is just a
// safety net).
const OPEN_CAP_MS = 1800
// Start the particle scene once the card's quick entrance animation has finished.
const SCENE_DELAY_MS = 480

const loadScene = () => import('./AnnouncementScene')
// three.js backdrop is code-split so the modal itself never waits on WebGL.
const AnnouncementScene = lazy(loadScene)

/**
 * Start downloading + decoding the banner as soon as this module loads (it's
 * imported by App, so this runs while the intro loader is still on screen).
 * Resolves when the image is ready to paint, or on error.
 */
const bannerReady: Promise<void> =
  typeof window === 'undefined'
    ? Promise.resolve()
    : new Promise<void>((resolve) => {
        const img = new Image()
        img.decoding = 'async'
        if (BANNER_SRCSET) {
          img.sizes = BANNER_SIZES
          img.srcset = BANNER_SRCSET
        }
        img.src = BANNER_SRC
        const done = () => resolve()
        img.onload = () => {
          if (typeof img.decode === 'function') img.decode().then(done, done)
          else done()
        }
        img.onerror = done
      })

/** Swap to the S3 original if an optimised WebP fails to load. */
const onBannerError = (e: SyntheticEvent<HTMLImageElement>) => {
  const img = e.currentTarget
  if (img.dataset.fallback) return
  img.dataset.fallback = '1'
  img.removeAttribute('srcset')
  img.src = BANNER_FALLBACK
}

type Mode = 'hidden' | 'modal' | 'bar' | 'dismissed'

function ReadMore({ className, onClick }: { className: string; onClick?: () => void }) {
  const { t } = useTranslation()
  const label = t('announcement.readMore', 'Read More')
  const arrow = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  )
  if (/^https?:\/\//.test(READ_MORE_HREF)) {
    return (
      <a href={READ_MORE_HREF} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick} aria-label={label}>
        <span>{label}</span>{arrow}
      </a>
    )
  }
  return (
    <Link to={READ_MORE_HREF} className={className} onClick={onClick} aria-label={label}>
      <span>{label}</span>{arrow}
    </Link>
  )
}

function AnnouncementBanner() {
  const { t } = useTranslation()
  // Shown on every page load / refresh, as requested — no persistence.
  const [mode, setMode] = useState<Mode>('hidden')
  const [closing, setClosing] = useState(false)
  const closeBtnRef = useRef<HTMLButtonElement>(null)
  const lastFocusRef = useRef<HTMLElement | null>(null)
  const tiltRef = useRef<HTMLDivElement>(null)
  const tiltRectRef = useRef<DOMRect | null>(null)
  const tiltPosRef = useRef<{ x: number; y: number } | null>(null)
  const tiltRafRef = useRef(0)
  const [reducedMotion] = useState(prefersReducedMotion)
  const [sceneOn, setSceneOn] = useState(false)

  // Pointer-driven 3D tilt + glare. Pointer events only record the position;
  // the CSS variables are written at most once per animation frame, and the
  // card's rect is measured once on enter instead of on every move.
  const applyTilt = () => {
    tiltRafRef.current = 0
    const el = tiltRef.current
    const rect = tiltRectRef.current
    const pos = tiltPosRef.current
    if (!el || !rect || !pos) return
    const px = Math.min(1, Math.max(0, (pos.x - rect.left) / rect.width))
    const py = Math.min(1, Math.max(0, (pos.y - rect.top) / rect.height))
    el.style.setProperty('--rx', `${(0.5 - py) * MAX_TILT * 2}deg`)
    el.style.setProperty('--ry', `${(px - 0.5) * MAX_TILT * 2}deg`)
    el.style.setProperty('--gx', `${px * 100}%`)
    el.style.setProperty('--gy', `${py * 100}%`)
    el.style.setProperty('--px', `${(px - 0.5) * -18}px`)
    el.style.setProperty('--py', `${(py - 0.5) * -18}px`)
  }

  const handleTiltEnter = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (reducedMotion || e.pointerType === 'touch') return
    const el = tiltRef.current
    if (!el) return
    tiltRectRef.current = el.getBoundingClientRect()
    el.classList.add('is-tilting')
  }

  const handleTiltMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (reducedMotion || e.pointerType === 'touch') return
    if (!tiltRectRef.current) handleTiltEnter(e)
    tiltPosRef.current = { x: e.clientX, y: e.clientY }
    if (!tiltRafRef.current) tiltRafRef.current = requestAnimationFrame(applyTilt)
  }

  const handleTiltLeave = () => {
    cancelAnimationFrame(tiltRafRef.current)
    tiltRafRef.current = 0
    tiltRectRef.current = null
    tiltPosRef.current = null
    const el = tiltRef.current
    if (!el) return
    el.style.setProperty('--rx', '0deg')
    el.style.setProperty('--ry', '0deg')
    el.style.setProperty('--px', '0px')
    el.style.setProperty('--py', '0px')
    el.classList.remove('is-tilting')
  }

  // Open after the homepage has had a chance to mount + paint, so the banner
  // never lands on a blank page ahead of the content. We wait for:
  //   1. a minimum delay (gives the lazy homepage chunk time to render), then
  //   2. the browser to go idle (page work settled), then
  //   3. the banner image to be decoded (preloaded in index.html, so instant).
  // A hard cap guarantees it opens even if the page stays busy.
  useEffect(() => {
    let cancelled = false
    const open = () => {
      if (!cancelled) setMode('modal')
    }

    // Idle = main thread free, i.e. the homepage has finished its first render.
    const waitForIdle = () =>
      new Promise<void>((resolve) => {
        const ric = (window as typeof window & {
          requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
        }).requestIdleCallback
        if (ric) ric(() => resolve(), { timeout: 800 })
        else window.setTimeout(resolve, 200)
      })

    const minDelay = new Promise<void>((r) => window.setTimeout(r, OPEN_DELAY_MS))
    const settled = minDelay
      .then(waitForIdle)
      .then(() => bannerReady)

    const cap = new Promise<void>((r) => window.setTimeout(r, OPEN_CAP_MS))

    Promise.race([settled, cap]).then(open)

    // Warm the three.js chunk in the background so it's ready when needed.
    if (!reducedMotion) loadScene().catch(() => {})
    return () => {
      cancelled = true
    }
  }, [reducedMotion])

  // Mount the particle scene only after the entrance animation, so WebGL setup
  // never competes with the card's first frames.
  useEffect(() => {
    if (mode !== 'modal' || reducedMotion) return
    const id = window.setTimeout(() => setSceneOn(true), SCENE_DELAY_MS)
    return () => {
      window.clearTimeout(id)
      setSceneOn(false)
    }
  }, [mode, reducedMotion])

  useEffect(() => () => cancelAnimationFrame(tiltRafRef.current), [])

  // Animate the modal out, then collapse it into the floating bar.
  const closeModal = useCallback(() => {
    setClosing(true)
    window.setTimeout(() => {
      setClosing(false)
      setMode('bar')
      lastFocusRef.current?.focus?.()
    }, 320)
  }, [])

  const reopenModal = () => {
    lastFocusRef.current = document.activeElement as HTMLElement | null
    setMode('modal')
  }

  // Modal side effects: scroll lock, Escape to close, initial focus.
  useEffect(() => {
    if (mode !== 'modal') return
    if (!lastFocusRef.current) lastFocusRef.current = document.activeElement as HTMLElement | null
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeBtnRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prevOverflow
      document.removeEventListener('keydown', onKey)
    }
  }, [mode, closeModal])

  if (mode === 'hidden' || mode === 'dismissed') return null

  if (mode === 'bar') {
    return (
      <div className="announce-bar" role="region" aria-label={t('announcement.label', 'Announcement')}>
        <button type="button" className="announce-bar__thumb" onClick={reopenModal} aria-label={t('announcement.view', 'View announcement')}>
          <img src={BANNER_THUMB} alt="" width={160} height={56} decoding="async" onError={onBannerError} />
        </button>
        <div className="announce-bar__text">
          <span className="announce-bar__badge">
            <span className="announce-bar__dot" aria-hidden="true" />
            {t('announcement.label', 'Announcement')}
          </span>
          <span className="announce-bar__title">{t('announcement.barTitle', "See what's new at BigData Ghana")}</span>
        </div>
        <ReadMore className="announce-bar__cta" />
        <button type="button" className="announce-bar__close" onClick={() => setMode('dismissed')} aria-label={t('announcement.dismiss', 'Dismiss announcement')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>
      </div>
    )
  }

  return (
    <div className={`announce-modal${closing ? ' is-closing' : ''}`}>
      <div className="announce-modal__backdrop" onClick={closeModal} aria-hidden="true" />
      {sceneOn && (
        <Suspense fallback={null}>
          <AnnouncementScene />
        </Suspense>
      )}
      <div
        className="announce-modal__card"
        role="dialog"
        aria-modal="true"
        aria-label={t('announcement.label', 'Announcement')}
        onPointerEnter={handleTiltEnter}
        onPointerMove={handleTiltMove}
        onPointerLeave={handleTiltLeave}
      >
        <div className="announce-modal__tilt" ref={tiltRef}>
          <button ref={closeBtnRef} type="button" className="announce-modal__close" onClick={closeModal} aria-label={t('announcement.close', 'Close announcement')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
          <div className="announce-modal__media">
            <div className="announce-modal__parallax">
              <img
                src={BANNER_SRC}
                srcSet={BANNER_SRCSET || undefined}
                sizes={BANNER_SRCSET ? BANNER_SIZES : undefined}
                width={BANNER_WIDTH}
                height={BANNER_HEIGHT}
                alt={t('announcement.imageAlt', 'BigData Ghana announcement')}
                decoding="async"
                fetchPriority="high"
                onError={onBannerError}
              />
            </div>
            <div className="announce-modal__glare" aria-hidden="true" />
          </div>
          <div className="announce-modal__footer">
            <span className="announce-modal__badge">
              <span className="announce-bar__dot" aria-hidden="true" />
              {t('announcement.label', 'Announcement')}
            </span>
            <ReadMore className="announce-modal__cta" onClick={closeModal} />
          </div>
        </div>
        <div className="announce-modal__shadow" aria-hidden="true" />
      </div>
    </div>
  )
}

export default AnnouncementBanner
